// Books — Rootegra LLC's complete books. One page, thirteen tabs, every write
// decided by the database (books_can_edit). Tabs live at /books/:tab.
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BOOKS_ITEMS, BOOKS_NAV, booksPath, type BooksTab } from './nav';
import './books.css';
import { useAuth, useBooksAccess } from '../../contexts/AuthContext';
import { OverviewTab } from './OverviewTab';
import { AppleTab } from './AppleTab';
import { BankingTab } from './BankingTab';
import { IncomeTab } from './IncomeTab';
import { SpendingTab } from './SpendingTab';
import { VendorsTab } from './VendorsTab';
import { AssetsTab } from './AssetsTab';
import { MembersTab } from './MembersTab';
import { JournalTab } from './JournalTab';
import { ReportsTab } from './ReportsTab';
import { PeriodsTab } from './PeriodsTab';
import { SettingsTab } from './SettingsTab';
import { AccessTab } from './AccessTab';

export type { BooksTab };

export function BooksHub() {
  const { tab: raw } = useParams();
  const nav = useNavigate();
  const { booksOnly } = useAuth();
  const { canEdit, isAdmin } = useBooksAccess();
  const item = BOOKS_ITEMS.find((t) => t.key === raw) ?? BOOKS_ITEMS[0];
  const tab = item.key;
  const go = (t: BooksTab) => nav(booksPath(t));
  const group = BOOKS_NAV.find((g) => g.items.some((i) => i.key === tab))?.group;
  const [newOpen, setNewOpen] = useState(false);
  const newRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!newOpen) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !newRef.current?.contains(e.target as Node)) setNewOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', close); };
  }, [newOpen]);

  useEffect(() => { window.scrollTo(0, 0); }, [tab]);

  const NEW_ITEMS: { label: string; to: BooksTab }[] = [
    { label: 'Journal entry', to: 'journal' },
    { label: 'Expense', to: 'spending' },
    { label: 'Other income', to: 'income' },
    { label: 'Member contribution', to: 'members' },
    { label: 'Member distribution', to: 'members' },
    { label: 'Fixed asset', to: 'assets' },
    { label: 'Bank statement import', to: 'banking' },
  ];

  const backTo = tab === 'overview' ? (booksOnly ? null : { label: 'Backbar', to: '/' }) : { label: 'Dashboard', to: '/books' };

  return (
    <div className="books">
      <header className="bk-head lp-no-print">
        <div className="bk-head-left">
          {backTo && (
            <button type="button" className="bk-back" onClick={() => nav(backTo.to)}>
              <span aria-hidden="true">‹</span> {backTo.label}
            </button>
          )}
          <div className="bk-crumb">Rootegra LLC{group ? ` · ${group}` : ''}</div>
          <h1 className="bk-title">{item.title}</h1>
          <p className="bk-blurb">{item.blurb}</p>
        </div>
        <div className="bk-head-right">
          {canEdit ? <span className="bk-pill bk-pill--ok">Can edit</span> : <span className="bk-pill">View only</span>}
          {isAdmin && <span className="bk-pill">Owner</span>}
          {canEdit && (
            <div className="bk-new" ref={newRef}>
              <button type="button" className="bk-btn bk-btn--primary" aria-haspopup="menu" aria-expanded={newOpen} onClick={() => setNewOpen((v) => !v)}>
                <span aria-hidden="true">+</span> New
              </button>
              {newOpen && (
                <div className="bk-menu" role="menu">
                  {NEW_ITEMS.map((n) => (
                    <button key={n.label} type="button" role="menuitem" onClick={() => { setNewOpen(false); go(n.to); }}>
                      {n.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </header>
      <div className="bk-print-head">Rootegra LLC · {item.title}</div>
      {tab === 'overview' && <OverviewTab go={go} />}
      {tab === 'apple' && <AppleTab />}
      {tab === 'banking' && <BankingTab />}
      {tab === 'income' && <IncomeTab />}
      {tab === 'spending' && <SpendingTab />}
      {tab === 'vendors' && <VendorsTab />}
      {tab === 'assets' && <AssetsTab />}
      {tab === 'members' && <MembersTab />}
      {tab === 'journal' && <JournalTab />}
      {tab === 'reports' && <ReportsTab />}
      {tab === 'periods' && <PeriodsTab />}
      {tab === 'settings' && <SettingsTab />}
      {tab === 'access' && <AccessTab />}
    </div>
  );
}
